import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function CapacitanceCalculator() {
  const [charge, setCharge] = useState("");
  const [voltage, setVoltage] = useState("");
  const [capacitance, setCapacitance] = useState("");
  const [resultC, setResultC] = useState<number | null>(null);
  const [resultQ, setResultQ] = useState<number | null>(null);
  const [resultV, setResultV] = useState<number | null>(null);

  const calculateCapacitance = () => {
    const q = parseFloat(charge);
    const v = parseFloat(voltage);

    if (isNaN(q) || isNaN(v) || v === 0) {
      toast.error("Please enter valid values (voltage must be non-zero)");
      return;
    }

    const c = q / v;
    setResultC(c);
    toast.success("Calculation complete!");
  };

  const calculateCharge = () => {
    const c = parseFloat(capacitance);
    const v = parseFloat(voltage);

    if (isNaN(c) || isNaN(v)) {
      toast.error("Please enter valid values");
      return;
    }

    const q = c * v;
    setResultQ(q);
    toast.success("Calculation complete!");
  };

  const calculateVoltage = () => {
    const q = parseFloat(charge);
    const c = parseFloat(capacitance);

    if (isNaN(q) || isNaN(c) || c === 0) {
      toast.error("Please enter valid values (capacitance must be non-zero)");
      return;
    }

    const v = q / c;
    setResultV(v);
    toast.success("Calculation complete!");
  };

  const reset = () => {
    setCharge("");
    setVoltage("");
    setCapacitance("");
    setResultC(null);
    setResultQ(null);
    setResultV(null);
  };

  return (
    <PageLayout calculatorName="Capacitance Calculator" calculatorPath="/calculator/capacitance">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Capacitance Calculator</h1>
          <p className="text-muted-foreground">
            Calculate capacitance, charge, or voltage
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Capacitance Formula: C = Q/V</CardTitle>
            <CardDescription>
              Where C is capacitance (F), Q is charge (C), and V is voltage (V)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Tabs defaultValue="capacitance" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="capacitance">Find C</TabsTrigger>
                <TabsTrigger value="charge">Find Q</TabsTrigger>
                <TabsTrigger value="voltage">Find V</TabsTrigger>
              </TabsList>

              <TabsContent value="capacitance" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="chargeC">Charge (C)</Label>
                    <Input
                      id="chargeC"
                      type="number"
                      step="0.000001"
                      placeholder="Enter charge in Coulombs"
                      value={charge}
                      onChange={(e) => setCharge(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="voltageC">Voltage (V)</Label>
                    <Input
                      id="voltageC"
                      type="number"
                      step="0.01"
                      placeholder="Enter voltage"
                      value={voltage}
                      onChange={(e) => setVoltage(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={calculateCapacitance} className="flex-1">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate
                  </Button>
                  <Button onClick={reset} variant="outline">
                    Reset
                  </Button>
                </div>

                {resultC !== null && (
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-xl">Result</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-primary">
                        {resultC.toExponential(2)} F
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Capacitance ({(resultC * 1e6).toFixed(2)} µF)
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="charge" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="capacitanceQ">Capacitance (F)</Label>
                    <Input
                      id="capacitanceQ"
                      type="number"
                      step="0.000001"
                      placeholder="Enter capacitance in Farads"
                      value={capacitance}
                      onChange={(e) => setCapacitance(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">1 µF = 0.000001 F</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="voltageQ">Voltage (V)</Label>
                    <Input
                      id="voltageQ"
                      type="number"
                      step="0.01"
                      placeholder="Enter voltage"
                      value={voltage}
                      onChange={(e) => setVoltage(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={calculateCharge} className="flex-1">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate
                  </Button>
                  <Button onClick={reset} variant="outline">
                    Reset
                  </Button>
                </div>

                {resultQ !== null && (
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-xl">Result</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-primary">
                        {resultQ.toExponential(2)} C
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Charge (Coulombs)
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="voltage" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="chargeV">Charge (C)</Label>
                    <Input
                      id="chargeV"
                      type="number"
                      step="0.000001"
                      placeholder="Enter charge in Coulombs"
                      value={charge}
                      onChange={(e) => setCharge(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="capacitanceV">Capacitance (F)</Label>
                    <Input
                      id="capacitanceV"
                      type="number"
                      step="0.000001"
                      placeholder="Enter capacitance in Farads"
                      value={capacitance}
                      onChange={(e) => setCapacitance(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">1 µF = 0.000001 F</p>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={calculateVoltage} className="flex-1">
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate
                  </Button>
                  <Button onClick={reset} variant="outline">
                    Reset
                  </Button>
                </div>

                {resultV !== null && (
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-xl">Result</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-primary">
                        {resultV.toFixed(2)} V
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        Voltage (Volts)
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>

            {(resultC !== null || resultQ !== null || resultV !== null) && (
              <CalculatorSuperCalcCTA config={calculatorCTAs["capacitance"]} trigger={resultC || resultQ || resultV} />
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">How to use</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>Choose what you want to calculate:</p>
                <p>• Capacitance: From charge and voltage</p>
                <p>• Charge: From capacitance and voltage</p>
                <p>• Voltage: From charge and capacitance</p>
                <p className="pt-2 font-medium text-foreground">
                  Example: 100 µF capacitor at 12 V stores 0.0012 C of charge
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
